import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Paper,
  Alert,
} from "@mui/material";
import { useUserContext } from "../../context/UserContext";
import {
  checkLetsLaunchAccess,
  getUserById,
  loginUser,
  initiateGoogleSignIn, // ADD THIS IMPORT
} from "../../client/api";
import LogoContainer from "../../components/LogoContainer";
import Logo from "../../components/Logo";
import useStyles from "./styles";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { setSsoCookie, getSafeRedirectTarget } from "../../utils/ssoCookie";

// SpikeUp Solutions Ops Command brand system - see spikeup-solutions-brand-book.html
const THEME_COLORS = {
  bg: "#04070a",
  panel: "#0b1318",
  panel2: "#0e1a20",
  bezel: "#1b262d",
  bezelHi: "#2c3b44",
  line: "#16242b",
  grnGlow: "rgba(55,255,163,.55)",
  cyan: "#39e0ff",
  cyanGlow: "rgba(57,224,255,.5)",
  red: "#ff4b3e",
  txt: "#9fc6c0",
  txtDim: "#5b7a78",
  ink: "#cdeee6",
} as const;

const FONT_DISPLAY = "'Orbitron', sans-serif";
const FONT_UI = "'Rajdhani', sans-serif";
const FONT_MONO = "'Share Tech Mono', monospace";

const Login: React.FC = () => {
  const classes = useStyles();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { user, setUser, setCurrentWorkspace } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkUserFromCookie = async () => {
      try {
        const authInfo = Cookies.get("authInfo");
        if (!authInfo) return;

        const { userId } = JSON.parse(authInfo);
        if (userId) {
          const userData = await getUserById(userId);
          if (userData) {
            setUser(userData);
            localStorage.setItem("userId", userId);

            // Silent SSO: another app (zapflow/farm-manager) bounced the
            // user here because it had no valid session - if letslaunch
            // already has one, refresh the shared cookie and send them
            // straight back rather than showing the login form again.
            const params = new URLSearchParams(location.search);
            const redirectTarget = getSafeRedirectTarget(params.get("redirect"));
            if (redirectTarget) {
              const existingToken = localStorage.getItem("token");
              if (existingToken) {
                setSsoCookie(existingToken);
                window.location.href = redirectTarget;
                return;
              }
            }

            const invitationUrl = localStorage.getItem("invitationUrl");
            if (invitationUrl) {
              localStorage.removeItem("invitationUrl");
              navigate(invitationUrl);
              return;
            }
            const org = userData.organization || {};
            const hasLetsLaunchAccess = !!org.letsLaunchAccess;
            const hasZapflowAccess = !!userData.zapflowAccess;
            const hasFarmSystemAccess = !!userData.farmSystemAccess;

            const APP_URLS: Record<string, string> = {
              zapflow: process.env.REACT_APP_ZAPFLOW_URL || "https://zapflow.letslaunch.io",
              farmsystem: process.env.REACT_APP_FARMSYSTEM_URL || "https://farmsystem.letslaunch.io",
            };
            const otherApps = [
              hasZapflowAccess && "zapflow",
              hasFarmSystemAccess && "farmsystem",
            ].filter(Boolean) as string[];
            const totalAccessCount = (hasLetsLaunchAccess ? 1 : 0) + otherApps.length;

            if (!hasLetsLaunchAccess && otherApps.length === 1) {
              window.location.href = APP_URLS[otherApps[0]];
            } else if (totalAccessCount > 1) {
              navigate("/app-selection");
            } else {
              navigate("/");
            }
          }
        }
      } catch (error) {
        console.error("Error checking user from cookie:", error);
      }
    };

    checkUserFromCookie();
  }, [navigate, setUser, location]);

  const handleLogin = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await loginUser(email, password);

      if (data.requiresVerification) {
        navigate(`/verify-email-pending?email=${encodeURIComponent(data.email)}`);
        return;
      }

      // Password was right; the MFA page finishes the login.
      if (data.mfaRequired) {
        navigate("/mfa", {
          state: {
            step: data.mfaStep,
            ticket: data.ticket,
            redirect: new URLSearchParams(location.search).get("redirect"),
          },
        });
        return;
      }

      setUser(data.user);
      setCurrentWorkspace(data.user.ownedWorkspaces[0]);

      const cookieData = {
        userId: data.user._id,
        organizationKey: data.user.organization.organizationKey,
        letsLaunchAccess: data.user.organization.letsLaunchAccess,
      };
      Cookies.set("authInfo", JSON.stringify(cookieData), {
        domain: process.env.REACT_APP_COOKIE_DOMAIN,
        path: "/",
        secure: true,
        sameSite: "strict",
        expires: 5,
      });

      localStorage.setItem("userId", data.user._id);
      if (data.token) {
        localStorage.setItem("token", data.token);
      }
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }

      // Another app (zapflow/farm-manager) bounced the user here for
      // SSO - set the shared cookie and send them straight back instead
      // of continuing to letslaunch's own home/app-selection screen.
      const redirectParams = new URLSearchParams(location.search);
      const redirectTarget = getSafeRedirectTarget(redirectParams.get("redirect"));
      if (redirectTarget && data.token) {
        setSsoCookie(data.token);
        window.location.href = redirectTarget;
        return;
      }

      const invitationUrl = localStorage.getItem("invitationUrl");
      if (invitationUrl) {
        localStorage.removeItem("invitationUrl");
        navigate(invitationUrl);
        return;
      }

      let hasLetsLaunchAccess = false;
      if (data.user.organization) {
        hasLetsLaunchAccess = await checkLetsLaunchAccess(
          data.user.organization.organizationKey
        );
      }
      const hasZapflowAccess = !!data.user.zapflowAccess;
      const hasFarmSystemAccess = !!data.user.farmSystemAccess;

      const APP_URLS: Record<string, string> = {
        zapflow: process.env.REACT_APP_ZAPFLOW_URL || "https://zapflow.letslaunch.io",
        farmsystem: process.env.REACT_APP_FARMSYSTEM_URL || "https://farmsystem.letslaunch.io",
      };

      const otherApps = [
        hasZapflowAccess && "zapflow",
        hasFarmSystemAccess && "farmsystem",
      ].filter(Boolean) as string[];

      const totalAccessCount = (hasLetsLaunchAccess ? 1 : 0) + otherApps.length;

      if (!hasLetsLaunchAccess && otherApps.length === 1) {
        // No LetsLaunch access, exactly one other app - go straight there.
        window.location.href = APP_URLS[otherApps[0]];
        return;
      }

      if (totalAccessCount > 1) {
        navigate("/app-selection");
      } else {
        navigate("/");
      }
    } catch (error: any) {
      setError(
        error.response?.data?.message ||
          "Login failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  // ADD THIS FUNCTION
  const handleGoogleSignIn = () => {
    initiateGoogleSignIn();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `radial-gradient(120% 80% at 50% -8%, #0a1820 0%, ${THEME_COLORS.bg} 55%), ${THEME_COLORS.bg}`,
        fontFamily: FONT_UI,
        padding: 2,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          mixBlendMode: "overlay",
          opacity: 0.45,
          background:
            "repeating-linear-gradient(0deg, rgba(0,0,0,.35) 0 1px, transparent 1px 3px)",
        },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 400,
          width: "100%",
          padding: 4,
          borderRadius: "11px",
          background: `linear-gradient(180deg, ${THEME_COLORS.panel2}, ${THEME_COLORS.panel})`,
          border: `1px solid ${THEME_COLORS.bezel}`,
          boxShadow: `inset 0 1px 0 ${THEME_COLORS.bezelHi}, inset 0 0 22px rgba(0,0,0,.6), 0 6px 18px rgba(0,0,0,.5)`,
          position: "relative",
          zIndex: 1,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            borderRadius: "11px 11px 0 0",
            background: THEME_COLORS.cyan,
            boxShadow: `0 0 12px ${THEME_COLORS.cyanGlow}`,
          },
        }}
      >
        {/* Logo Section */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <LogoContainer>
            <Logo />
          </LogoContainer>
          <Typography
            variant="h4"
            sx={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 900,
              letterSpacing: "3px",
              color: THEME_COLORS.ink,
              textShadow: `0 0 20px ${THEME_COLORS.grnGlow}`,
              mb: 1,
            }}
          >
            LetsLaunch
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: FONT_MONO,
              color: THEME_COLORS.txtDim,
              letterSpacing: "2px",
              fontSize: "11px",
              textTransform: "uppercase",
            }}
          >
            Welcome back! Sign in to continue
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              backgroundColor: "rgba(255,75,62,0.08)",
              border: `1px solid ${THEME_COLORS.red}`,
              borderLeft: `4px solid ${THEME_COLORS.red}`,
              color: THEME_COLORS.ink,
              borderRadius: "8px",
              fontFamily: FONT_UI,
              "& .MuiAlert-icon": { color: THEME_COLORS.red },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Login Form */}
        <Box component="form" onKeyPress={handleKeyPress}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2.5 }}
            InputProps={{
              sx: {
                borderRadius: "6px",
                backgroundColor: "#040b09",
                color: THEME_COLORS.ink,
                fontFamily: FONT_MONO,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: THEME_COLORS.line,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: THEME_COLORS.bezelHi,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: THEME_COLORS.cyan,
                  boxShadow: "0 0 0 2px rgba(57,224,255,0.15)",
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: THEME_COLORS.txtDim,
                fontFamily: FONT_UI,
                fontWeight: 500,
                "&.Mui-focused": { color: THEME_COLORS.cyan },
              },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              sx: {
                borderRadius: "6px",
                backgroundColor: "#040b09",
                color: THEME_COLORS.ink,
                fontFamily: FONT_MONO,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: THEME_COLORS.line,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: THEME_COLORS.bezelHi,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: THEME_COLORS.cyan,
                  boxShadow: "0 0 0 2px rgba(57,224,255,0.15)",
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: THEME_COLORS.txtDim,
                fontFamily: FONT_UI,
                fontWeight: 500,
                "&.Mui-focused": { color: THEME_COLORS.cyan },
              },
            }}
          />

          {/* Forgot Password Link */}
          <Box sx={{ textAlign: "right", mb: 3 }}>
            <Button
              variant="text"
              href="/request-password-reset"
              sx={{
                textTransform: "none",
                fontSize: "0.875rem",
                fontFamily: FONT_UI,
                fontWeight: 500,
                color: THEME_COLORS.txtDim,
                "&:hover": {
                  color: THEME_COLORS.cyan,
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Forgot your password?
            </Button>
          </Box>

          {/* Login Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            disabled={loading}
            sx={{
              py: 1.5,
              borderRadius: "7px",
              background: "linear-gradient(180deg, #13343f, #0a1c22)",
              border: `1px solid ${THEME_COLORS.bezelHi}`,
              color: THEME_COLORS.cyan,
              fontFamily: FONT_DISPLAY,
              fontSize: "0.85rem",
              fontWeight: 700,
              letterSpacing: "2px",
              textShadow: `0 0 10px ${THEME_COLORS.cyanGlow}`,
              boxShadow: "none",
              mb: 2, // Changed from mb: 3 to mb: 2 to add space for Google button
              "&:hover": {
                background: "linear-gradient(180deg, #17404c, #0d232a)",
                borderColor: THEME_COLORS.cyan,
                boxShadow: "0 0 14px rgba(57,224,255,0.25)",
              },
              "&:disabled": {
                background: THEME_COLORS.panel,
                borderColor: THEME_COLORS.bezel,
                color: THEME_COLORS.txtDim,
                textShadow: "none",
                boxShadow: "none",
              },
            }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>

          {/* ADD GOOGLE SIGN-IN BUTTON HERE */}
          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleSignIn}
            sx={{
              py: 1.5,
              borderRadius: "7px",
              background: "linear-gradient(160deg, #101e23, #0a1418)",
              borderColor: THEME_COLORS.bezel,
              color: THEME_COLORS.txt,
              fontFamily: FONT_UI,
              fontSize: "1rem",
              fontWeight: 600,
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              "&:hover": {
                borderColor: THEME_COLORS.bezelHi,
                color: THEME_COLORS.ink,
                backgroundColor: "rgba(57,224,255,0.04)",
              },
            }}
          >
            <img
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="Google"
              style={{ width: 20, height: 20, borderRadius: "10px" }}
            />
            Sign in with Google
          </Button>

          <Divider
            sx={{
              mb: 3,
              "&::before, &::after": {
                borderColor: THEME_COLORS.line,
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: THEME_COLORS.txtDim,
                fontFamily: FONT_MONO,
                fontSize: "11px",
                letterSpacing: "1px",
              }}
            >
              or
            </Typography>
          </Divider>

          {/* Sign Up Section */}
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="body2"
              sx={{ color: THEME_COLORS.txtDim, fontFamily: FONT_UI, mb: 2 }}
            >
              Don't have an account?
            </Typography>
            <Button
              variant="outlined"
              href="/signup"
              sx={{
                textTransform: "none",
                borderRadius: "7px",
                borderColor: THEME_COLORS.bezelHi,
                color: THEME_COLORS.ink,
                background: "linear-gradient(160deg, #101e23, #0a1418)",
                px: 4,
                py: 1,
                fontFamily: FONT_UI,
                fontWeight: 600,
                "&:hover": {
                  borderColor: THEME_COLORS.cyan,
                  boxShadow: "0 0 14px rgba(57,224,255,0.2)",
                },
              }}
            >
              Sign Up Now
            </Button>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{
              color: THEME_COLORS.txtDim,
              fontFamily: FONT_MONO,
              letterSpacing: "1px",
              fontSize: "10px",
            }}
          >
            © 2026 LetsLaunch
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
